/**
 * A huddle ring reaching a background tab (browser only; platform/ringAlert.ts): the browser notification with
 * Join / Decline and the flashing tab title, both gone when the ring ends. Mounted beside IncomingHuddle (the card),
 * whose Join it mirrors when the notification's Join is clicked.
 */
import { useEffect } from "react";
import { Shell, type ShellViewModel } from "@bundle/core";
import { useHuddle, useT, useViewModel, useWorkspace } from "@bundle/ui";
import { isDesktop } from "../platform/desktop.ts";
import { webPush } from "../platform/push.ts";
import { closeRingNotice, flashTitle, isRingAction, ringNotifyDecision, showRingNotice } from "../platform/ringAlert.ts";
import { usePlatform } from "./PlatformContext.ts";

export function RingAlerts({ shell, slug, paused }: { shell: ShellViewModel; slug: string; paused: () => boolean }) {
  const t = useT();
  const huddle = useHuddle();
  const ws = useWorkspace();
  const notifications = usePlatform().notifications;
  const status = useViewModel(notifications).status;
  const ring = huddle?.state.incoming;
  const caller = ring === undefined ? undefined : (ws.state.cache?.members[ring.callerId]?.displayName ?? ring.title);
  const browser = !isDesktop() && huddle !== null;

  // Join / Decline from the notification (sw.ts), for the ring on screen; Join after it ended opens its huddle.
  useEffect(() => {
    if (!browser || !("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      if (!isRingAction(e.data)) return;
      const { action, huddleId, url } = e.data;
      const now = huddle.vm.state.incoming;
      if (now?.huddleId !== huddleId) {
        if (action === "join" && Shell.parseUrl(url)?.ws === slug) shell.open(url, "current");
        return;
      }
      if (action === "decline") {
        huddle.vm.declineRing();
      } else if (huddle.browserOnly) {
        huddle.vm.declineRing();
        huddle.openInBrowser(now.channelId);
      } else {
        void huddle.vm.acceptRing();
        shell.open(Shell.formatUrl({ type: "huddle", ws: slug, huddleId: now.channelId }), "current");
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [browser, huddle, shell, slug]);

  const huddleId = ring?.huddleId;
  const channelId = ring?.channelId;

  // The notification: when the ring arrives in the background, or when the window loses focus while it rings.
  useEffect(() => {
    if (!browser || ring === undefined || !("Notification" in window)) return;
    let shown = false;
    const maybeShow = () => {
      if (shown) return;
      const show = ringNotifyDecision({
        permission: Notification.permission,
        webPush: webPush.availability() === "available",
        pushOn: status === "on",
        visible: document.visibilityState === "visible",
        focused: document.visibilityState === "visible" && document.hasFocus(),
        paused: paused(),
      });
      if (!show) return;
      shown = true;
      const notice = {
        title: ring.title,
        body: ring.body,
        channelId: ring.channelId,
        huddleId: ring.huddleId,
        huddleUrl: Shell.formatUrl({ type: "huddle", ws: slug, huddleId: ring.channelId }),
        joinLabel: t("huddle.incoming.join"),
        declineLabel: t("huddle.incoming.decline"),
      };
      void showRingNotice(notice).catch((err: unknown) => console.warn("ring notification failed", err));
    };
    maybeShow();
    window.addEventListener("blur", maybeShow);
    return () => {
      window.removeEventListener("blur", maybeShow);
      // The ring ended (or another replaced it): its notification goes too, FCM's included.
      void closeRingNotice(ring.channelId).catch(() => undefined);
    };
    // One notification per ring; the labels and status at its start are the ones it keeps.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [browser, huddleId, channelId]);

  // The tab title flashes while the ring is on and the tab is hidden.
  const flash = caller === undefined ? undefined : t("notifications.ring.tabTitle", { name: caller });
  useEffect(() => {
    if (!browser || flash === undefined) return;
    let stop: (() => void) | undefined;
    const sync = () => {
      if (document.visibilityState === "hidden") stop ??= flashTitle(flash);
      else {
        stop?.();
        stop = undefined;
      }
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      stop?.();
    };
  }, [browser, flash, huddleId]);

  return null;
}
